import { useEffect, useRef } from 'react'
import { Marker, Popup } from 'react-leaflet'
import L from 'leaflet'

interface SmoothMarkerProps {
  position: [number, number]
  icon: L.DivIcon | L.Icon
  children?: React.ReactNode
  duration?: number // animation duration in ms (default 1000)
  eventHandlers?: L.LeafletEventHandlerFnMap
}

/**
 * A Leaflet Marker that smoothly animates between position changes
 * using CSS transitions instead of jumping instantly.
 */
export function SmoothMarker({ position, icon, children, duration = 1000, eventHandlers }: SmoothMarkerProps) {
  const markerRef = useRef<L.Marker>(null)
  const prevPos = useRef<[number, number]>(position)

  useEffect(() => {
    const marker = markerRef.current
    if (!marker) return

    // Get the marker's DOM element and add CSS transition
    const el = marker.getElement()
    if (el) {
      el.style.transition = `transform ${duration}ms ease-in-out`
    }

    // Only animate if position actually changed
    if (prevPos.current[0] !== position[0] || prevPos.current[1] !== position[1]) {
      marker.setLatLng(position)
      prevPos.current = position
    }
  }, [position[0], position[1], duration])

  return (
    <Marker ref={markerRef} position={position} icon={icon} eventHandlers={eventHandlers}>
      {children}
    </Marker>
  )
}
